import React, { useEffect, useMemo, useRef, useState } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";
import { FinderFeedsPanel } from "@/components/admin/FinderFeedsPanel";

/**
 * Admin Desk · Sources — Msg 74 item 4.
 *
 * Wraps POST /api/admin/sources/precheck for one URL at a time and, when
 * the operator pastes a newline / comma list, runs each URL in sequence
 * (never in parallel — pre-check hits third-party robots.txt + sitemap).
 *
 * The table is a build-time output the founder can read AND export. It is
 * strictly a report; nothing here mutates the live seed list. Adding a
 * source to the crawler still happens in Settings.
 */

const SEED_URLS = [
  "https://hustlesasa.com",
  "https://hustlesasa.com/sitemap.xml",
  "https://hustlesasa.com/events",
  "https://ticketsasa.com",
  "https://ticketsasa.com/events",
  "https://ticketsasa.com/sitemap.xml",
  "https://quicket.co.ke",
  "https://quicket.co.ke/sitemap.xml",
  "https://eventbrite.com/d/kenya--nairobi/events/",
  "https://kenyabuzz.com/events/",
  "https://kenyabuzz.com/sitemap.xml",
  "https://eatout.co.ke",
  "https://eatout.co.ke/sitemap.xml",
  "https://unseennairobi.com",
  "https://unseennairobi.com/sitemap.xml",
  "https://thealchemist.co.ke",
  "https://thealchemist.co.ke/sitemap.xml",
  "https://thealchemist.co.ke/whats-on",
  "https://kenyanationaltheatre.go.ke",
  "https://kenyanationaltheatre.go.ke/events",
  "https://museums.or.ke",
  "https://thejunctionmall.co.ke",
  "https://thejunctionmall.co.ke/events",
  "https://mookh.com/sitemap.xml",
  "https://mookh.com/calendar/",
];

const parseList = (raw) => (raw || "")
  .split(/[\n,]/)
  .map((s) => s.trim())
  .filter(Boolean);

const cellVerdict = (row) => {
  if (!row) return "";
  if (row.error) return row.error;
  return row.verdict || "—";
};

const cellStatus = (row) => {
  if (!row) return "queued";
  if (row.error) return "error";
  const s = row.fetch?.http_status;
  const rob = row.robots?.http_status;
  if (s == null) return "unreachable";
  return `HTTP ${s}${rob != null ? ` · robots ${rob}` : ""}`;
};

const chip = (label, tone) => {
  const bg = { green: "#0E8148", red: "#B33A3A", amber: "#FFD60A", grey: "#2F2F52" }[tone] || "#2F2F52";
  const fg = tone === "amber" ? "#1A1A30" : "#FFFFFF";
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.16em]"
          style={{ background: bg, color: fg, fontFamily: "var(--f-head)", fontWeight: 700 }}>
      {label}
    </span>
  );
};

const toneFor = (row) => {
  if (!row) return "grey";
  if (row.error) return "red";
  const v = (row.verdict || "").toLowerCase();
  if (v.includes("ready to add") || v.includes("looks good")) return "green";
  if (v.includes("unreachable") || v.startsWith("http 4") || v.startsWith("http 5")) return "red";
  return "amber";
};

export default function SourcesPage() {
  const [pasted, setPasted] = useState(SEED_URLS.join("\n"));
  const [rows, setRows] = useState({});
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const urls = useMemo(() => parseList(pasted), [pasted]);

  const runAll = async () => {
    if (!urls.length) return toast.error("paste at least one URL");
    setRunning(true);
    setProgress({ done: 0, total: urls.length });
    const next = {};
    for (let i = 0; i < urls.length; i++) {
      const u = urls[i];
      try {
        const r = await client.post("/admin/sources/precheck", { url: u });
        next[u] = r.data;
      } catch (e) {
        next[u] = { url: u, error: e?.response?.data?.detail || e?.message || "request failed" };
      }
      setRows({ ...next });
      setProgress({ done: i + 1, total: urls.length });
    }
    setRunning(false);
    toast.success(`checked ${urls.length} sources`);
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ww-sources-precheck-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportMd = () => {
    const lines = ["| source | status | verdict | recommendation |", "| --- | --- | --- | --- |"];
    urls.forEach((u) => {
      const r = rows[u];
      const status = cellStatus(r).replace(/\|/g, "\\|");
      const verdict = cellVerdict(r).replace(/\|/g, "\\|");
      const rec = (r?.recommendation || "").replace(/\|/g, "\\|");
      lines.push(`| ${u} | ${status} | ${verdict} | ${rec} |`);
    });
    const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ww-sources-precheck-${new Date().toISOString().slice(0,10)}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-6xl flex-col gap-6 p-8" data-testid="sources-page">
      <header className="flex items-start justify-between">
        <div>
          <p className="label-tiny">Admin desk</p>
          <h1 className="mt-1 text-2xl font-display">Ingestion sources · pre-check</h1>
          <p className="mt-2 max-w-2xl text-sm text-neutral-600">
            Dry-run any candidate URL or sitemap. Reports robots.txt, HTTP status, whether the page carries
            schema.org Event JSON-LD, and (for sitemaps) how many child URLs do. Nothing is added to the
            live crawler from this page.
          </p>
        </div>
      </header>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <label className="label-tiny">URLs (one per line or comma-separated)</label>
        <textarea
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          data-testid="sources-urls-input"
          rows={8}
          className="mt-2 w-full rounded-md border border-neutral-300 p-3 font-mono text-sm"
          disabled={running}
        />
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            data-testid="sources-run"
            onClick={runAll}
            disabled={running || urls.length === 0}
            className="rounded-md bg-neutral-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            {running ? `checking ${progress.done}/${progress.total}…` : `run pre-check on ${urls.length} URL${urls.length === 1 ? "" : "s"}`}
          </button>
          <RunFinderNowButton />
          <button
            data-testid="sources-export-md"
            onClick={exportMd}
            disabled={Object.keys(rows).length === 0}
            className="rounded-md border border-neutral-300 px-3 py-2 text-sm disabled:opacity-40"
          >export markdown</button>
          <button
            data-testid="sources-export-json"
            onClick={exportJson}
            disabled={Object.keys(rows).length === 0}
            className="rounded-md border border-neutral-300 px-3 py-2 text-sm disabled:opacity-40"
          >export json</button>
        </div>
        <p className="mt-2 text-xs text-neutral-600">
          <strong>pre-check</strong> is a dry-run — only reports whether each URL is reachable, whether robots.txt allows us, and whether the page has JSON-LD events. It never writes to the queue.<br/>
          <strong>run the finder now</strong> fetches, extracts, dedupes and puts pulled items into the <strong>review queue only</strong>. Nothing publishes without a human approve. Pressing again while it's running is a no-op.
        </p>
      </section>

      <CrawlerRunsTable />
      <FinderFeedsPanel />

      <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm" data-testid="sources-table">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-[0.18em] text-neutral-500">
              <tr>
                <th className="p-3">source</th>
                <th className="p-3">status</th>
                <th className="p-3">verdict</th>
                <th className="p-3">recommendation</th>
              </tr>
            </thead>
            <tbody>
              {urls.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-neutral-500">paste one or more URLs to check</td></tr>
              )}
              {urls.map((u) => {
                const r = rows[u];
                const tone = toneFor(r);
                return (
                  <tr key={u} className="border-t border-neutral-100 align-top" data-testid={`sources-row-${u}`}>
                    <td className="p-3 font-mono text-[12px]">
                      <a href={u} target="_blank" rel="noreferrer" className="hover:underline">{u}</a>
                    </td>
                    <td className="p-3">
                      {r ? chip(cellStatus(r), tone) : chip("queued", "grey")}
                    </td>
                    <td className="p-3">{cellVerdict(r)}</td>
                    <td className="p-3 text-neutral-600">{r?.recommendation || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/** Wave 12 (Vania N4 · 1 Oct) · "run the finder now" button on /admin/sources.
 *  Idempotent (backend refuses parallel runs within a 10-minute window), shows
 *  live progress + per-batch totals polled from /jsonld/status. Pulled items
 *  go to the review queue only; nothing is auto-published. */
function RunFinderNowButton() {
  const [status, setStatus] = useState({ running: false, totals: {}, last_batch_started_at: null });
  const [kicking, setKicking] = useState(false);
  const pollRef = useRef(null);

  const poll = async () => {
    try {
      const r = await client.get("/admin/sources/jsonld/status");
      setStatus(r.data);
    } catch { /* ignore polling errors */ }
  };

  // WW-200 · one check on open; poll every 4 s only while a run is going, stop when it ends.
  useEffect(() => { poll(); }, []);
  useEffect(() => {
    if (!status.running) return undefined;
    pollRef.current = setInterval(poll, 4000);
    return () => clearInterval(pollRef.current);
  }, [status.running]);

  const trigger = async () => {
    setKicking(true);
    try {
      const r = await client.post("/admin/sources/jsonld/run", {});
      if (r.data.already_running) {
        toast.message(r.data.message || "the finder is already running");
      } else {
        toast.success(`finder running — scanning ${r.data.urls_scanned} sources. items will land in the review queue.`);
      }
      poll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "finder failed to start");
    } finally { setKicking(false); }
  };

  const running = status.running || kicking;
  const t = status.totals || {};
  const lastAt = status.last_batch_started_at
    ? (status.last_batch_started_at || "").replace("T", " ").slice(0, 16)
    : "";

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <button data-testid="sources-run-finder-now" onClick={trigger} disabled={running}
              className="rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-60"
              style={{ background: "#FFD60A", color: "#1A1A30",
                       cursor: running ? "progress" : "pointer" }}>
        {running
          ? (kicking ? "kicking off…" : "finder running…")
          : "run the finder now"}
      </button>
      {(t.runs || 0) > 0 && (
        <p data-testid="finder-status-summary"
           className="text-xs text-neutral-600 lowercase">
          {running ? "scanning · " : "last run · "}
          {t.runs} source{t.runs === 1 ? "" : "s"} · {t.items_new || 0} new · {t.items_deduped || 0} deduped · {t.items_failed || 0} failed
          {lastAt && !running && <span> · {lastAt}</span>}
        </p>
      )}
    </div>
  );
}

function CrawlerRunsTable() {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try {
      const r = await client.get("/admin/sources/jsonld/runs");
      setRows((r.data.runs || []).slice(0, 20));
    } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);
  return (
    <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-testid="crawler-runs">
      <div className="border-b border-neutral-100 px-4 py-3">
        <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">recent crawler runs (auto-refresh)</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-[0.16em] text-neutral-500">
            <tr>
              <th className="p-3">source</th><th className="p-3">started</th><th className="p-3">finished</th>
              <th className="p-3">events</th><th className="p-3">new</th><th className="p-3">deduped</th><th className="p-3">error</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-neutral-500">no crawler runs yet. hit "crawl now".</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-neutral-100">
                <td className="p-3 font-mono text-[11px]">{r.source_url || r.seed_url || "—"}</td>
                <td className="p-3 text-xs text-neutral-500">{(r.started_at || "").replace("T"," ").slice(0,19)}</td>
                <td className="p-3 text-xs text-neutral-500">{r.finished_at ? (r.finished_at || "").replace("T"," ").slice(0,19) : <em>running…</em>}</td>
                <td className="p-3">{r.events_found ?? r.raw_input_count ?? "—"}</td>
                <td className="p-3">{r.pending_created ?? r.new_pending_count ?? "—"}</td>
                <td className="p-3">{r.deduped_count ?? "—"}</td>
                <td className="p-3 text-xs text-red-600">{r.error || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
